import { FC } from 'react'

import { Currency } from '@sequencehq/api'
import { formatCurrencyInput, toCurrencySymbol } from '@sequencehq/utils'

import { PERCENTAGE_UNIT } from 'Automations/nodeDefinitions/conditions'
import { NumberInputField } from 'Automations/nodeDefinitions/components'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'

import { clampNumeric } from '../clampNumeric'

export type PriceValueFieldProps = {
  'data-testid'?: string
  value: string
  /** Drives the prefix/suffix and the blur-time clamping rules. */
  unit: string | undefined
  onChange: (amount: string) => void
  validationErrors?: ValidationResult[]
}

/**
 * Number input paired with a price unit. When the unit is the percentage
 * sentinel, the field shows a `%` suffix and clamps to 0–100 on blur;
 * otherwise it renders the unit's currency symbol as a prefix and clamps
 * to ≥ 0 with locale-formatted currency on blur.
 */
export const PriceValueField: FC<PriceValueFieldProps> = ({
  'data-testid': dataTestId,
  value,
  unit,
  onChange,
  validationErrors
}) => {
  const isPercentage = unit === PERCENTAGE_UNIT
  const currencySymbol =
    !isPercentage && unit ? toCurrencySymbol(unit as Currency) : undefined

  return (
    <NumberInputField
      data-testid={dataTestId}
      value={value}
      placeholder="0.00"
      textAlign="right"
      prefix={currencySymbol}
      suffix={isPercentage ? '%' : undefined}
      onChange={e => onChange(e.target.value)}
      onBlur={() => {
        if (isPercentage) {
          const clamped = clampNumeric(value, { min: 0, max: 100 })
          onChange(String(clamped))
          return
        }
        const clamped = clampNumeric(value, { min: 0 })
        onChange(formatCurrencyInput(String(clamped), 'currency'))
      }}
      validationErrors={validationErrors}
    />
  )
}
